<template>
  <div class="assistant-card" :class="`assistant-card--${card.kind}`">
    <template v-if="card.kind === 'status'">
      <p class="assistant-card__eyebrow">{{ t('assistant.cards.status') }}</p>
      <p class="assistant-card__title">{{ card.status_text }}</p>
      <ol class="assistant-steps" :aria-label="t('assistant.cards.progress')">
        <li v-for="(step, i) in steps" :key="step" :class="{ done: i <= stepIndex, current: i === stepIndex }">
          <span class="assistant-steps__dot" aria-hidden="true" />
          <span class="assistant-steps__label">{{ t(`steps.${step}`) }}</span>
        </li>
      </ol>
      <div v-if="latest" class="assistant-card__latest">
        <p class="assistant-card__strong">{{ latest.beskrivning }}</p>
        <p class="assistant-card__muted">{{ [latest.plats, when(latest.tid)].filter(Boolean).join(' · ') }}</p>
      </div>
      <p v-else class="assistant-card__muted">{{ t('assistant.cards.noEvents') }}</p>
      <p v-if="card.transportor" class="assistant-card__muted">
        {{ capitalizeFirstLetter(t('carrier')) }}: {{ carrierName(String(card.transportor)) }}
      </p>
    </template>

    <template v-else-if="card.kind === 'timeline'">
      <p class="assistant-card__eyebrow">{{ t('assistant.cards.journey') }}</p>
      <ol v-if="events.length" class="assistant-timeline">
        <li v-for="(e, i) in events" :key="i" :class="{ latest: i === 0 }">
          <p class="assistant-card__strong">{{ e.beskrivning || e.status_text }}</p>
          <p class="assistant-card__muted">{{ [e.plats, when(e.tid)].filter(Boolean).join(' · ') }}</p>
        </li>
      </ol>
      <p v-else class="assistant-card__muted">{{ t('assistant.cards.noEvents') }}</p>
    </template>

    <template v-else-if="card.kind === 'delivery'">
      <p class="assistant-card__eyebrow">{{ t('assistant.cards.delivery') }}</p>
      <div v-for="(p, i) in servicePoints" :key="i" class="assistant-card__block">
        <p class="assistant-card__strong">{{ p.namn }}</p>
        <p class="assistant-card__muted">
          {{ [p.adress, [p.postnummer, p.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') }}
        </p>
      </div>
      <!-- The pick-up address repeats the service point; show it only when there is none -->
      <p v-if="card.upphamtningsplats && !servicePoints.length" class="assistant-card__strong">
        {{ card.upphamtningsplats }}
      </p>
      <p v-if="!servicePoints.length && !card.upphamtningsplats" class="assistant-card__muted">
        {{ t('assistant.cards.noServicePoint') }}
      </p>
      <a
        v-if="card.transportorens_sparningslank"
        class="assistant-card__link"
        :href="String(card.transportorens_sparningslank)"
        target="_blank"
        rel="noopener"
      >
        {{ t('assistant.cards.carrierLink') }}
      </a>
    </template>

    <template v-else-if="card.kind === 'merchant'">
      <p class="assistant-card__eyebrow">{{ t('assistant.cards.merchant') }}</p>
      <p class="assistant-card__title">{{ card.namn || t('assistant.cards.theStore') }}</p>
      <p class="assistant-card__note">{{ t('assistant.cards.shippingOnly', { brand }) }}</p>
      <p v-if="card.ordernummer_hos_handlaren" class="assistant-card__muted">
        {{ t('assistant.cards.orderRef') }}: {{ card.ordernummer_hos_handlaren }}
      </p>
      <p v-if="card.epost" class="assistant-card__muted">{{ card.epost }}</p>
      <div v-if="card.epost" class="assistant-card__actions">
        <button type="button" class="assistant-card__button" @click="emit('forward')">
          {{ t('assistant.cards.forward') }}
        </button>
        <a class="assistant-card__link" :href="`mailto:${card.epost}`">{{ t('assistant.cards.emailSelf') }}</a>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { AssistantCard } from '@/services/AssistantClient'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalizeFirstLetter, carrierName } from '@/helpers/Utils'
import moment from 'moment'

interface CardEvent {
  tid?: string | null
  status?: string | null
  status_text?: string | null
  beskrivning?: string | null
  plats?: string | null
}

interface ServicePoint {
  namn?: string | null
  adress?: string | null
  postnummer?: string | null
  ort?: string | null
}

const props = defineProps<{ card: AssistantCard; brand: string }>()
// "forward": the customer wants the assistant to send their case to the store
const emit = defineEmits<{ forward: [] }>()
const { t, locale } = useI18n()

const steps = ['booked', 'in_transit', 'out_for_delivery', 'delivered']
const stepIndex = computed(() => {
  const status = String(props.card.status || '')
  if (status === 'delivered' || status === 'returned') return 3
  if (status === 'out_for_delivery') return 2
  if (['in_transit', 'exception', 'failed_attempt'].includes(status)) return 1
  return 0
})
const latest = computed(() => (props.card.senaste_handelse as CardEvent | null) || null)
const events = computed(() => (props.card.handelser as CardEvent[] | undefined) || [])
const servicePoints = computed(() => (props.card.utlamningsstallen as ServicePoint[] | undefined) || [])

const when = (time?: string | null): string => (time ? moment(time).locale(locale.value).calendar() : '')
</script>
